import React, { FC, useEffect, useState } from 'react'
import { Box, Menu, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils';
import { columns } from '../../../dtos/columns';
import { FiltroBandejaCompleto } from '../../../features/bandejaMutuos/models/filtroBandeja';
import { FiltroCarteraCompleto } from '../../../features/carteraTotal/models/filtroCartera';
import { TablaBasicFilter } from '../../../utils/tableBasicFilter';

interface TablaProps<T> {
  url: string
  columns: columns<T>[]
  titulo: React.ReactNode
  conditionalRowStyles?: any
  onRowClicked?: any
  control: controlProps<T>
  callApi?: boolean;
  pointerOnHover?: boolean | undefined
  setFiltros?: React.Dispatch<React.SetStateAction<TablaBasicFilter>>
  contextMenuRender?: (row: T, handleClose: () => void) => React.ReactNode
  rowsPerPageOptions?: (number | { label: string; value: number })[]
}

export type SortOrder = 'asc' | 'desc';

export interface controlProps<T> {
  data: never[]
  loading: boolean
  totalRows: number
  perPage: number
  page: number
  order: SortOrder
  params: URLSearchParams
  fetchData: (pageParam?: number) => Promise<void>
  getData: (url: string) => Promise<void>
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
  setPage: React.Dispatch<React.SetStateAction<number>>
  setOrder: React.Dispatch<React.SetStateAction<SortOrder>>
  setPerPage: React.Dispatch<React.SetStateAction<number>>
  esError?: boolean
}

const Tabla = <T,>({ url, columns, titulo, control, setFiltros, callApi = true, contextMenuRender, rowsPerPageOptions }: TablaProps<T>) => {

  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
  } | null>(null);
  const [selectedRow, setSelectedRow] = useState<T | null>(null);

  useEffect(() => {
    if (callApi)
      control.fetchData()
  }, [])

  useEffect(() => {
    if (contextMenu && control.loading) {
      handleCloseContextMenu();
    }
  }, [control.loading, contextMenu]);

  const handleContextMenu = (event: React.MouseEvent, row: T) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 2,
    });
    setSelectedRow(row);
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
    setSelectedRow(null);
  };

  const getRowKey = (row: T, index: number) => {
    const rowAny = row as any;
    if (rowAny?.id !== undefined && rowAny?.id !== null) return String(rowAny.id);
    if (rowAny?.numerosolicitud !== undefined && rowAny?.numerosolicitud !== null) return String(rowAny.numerosolicitud);
    return `row-${index}`;
  };

  const getColumnKey = (column: columns<T>, index: number) => {
    if (column.sortField) return `col-${String(column.sortField)}`;
    if (typeof column.name === 'string') return `col-${column.name}`;
    return `col-${index}`;
  };

  const handleChangePage = (event: any, page: number) => {
    control.fetchData(page)
  };

  const handleChangeRowsPerPage = async (event: any) => {

    control.setLoading(true)
    control.setPage(0);

    const newPerPage = parseInt(event.target.value, 10);
    const skip = control.perPage * (control.page - 0)

    control.params.delete('skip')
    control.params.append('skip', skip.toString())

    control.params.delete('take')
    control.params.append('take', newPerPage.toString())

    if (control.params.toString()) {
      url += '?' + control.params.toString()
    }

    control.setPerPage(newPerPage);

    await control.getData(url)
  };

  const createSortHandler =
    (property: any) => (event: React.MouseEvent<unknown>) => {
      handleRequestSort(event, property);
    };

  const handleRequestSort = async (
    event: React.MouseEvent<unknown>,
    property: any,
  ) => {

    const isAsc = control.params.get('sort') === property && control.params.get('order') === 'asc';
    const newOrder = isAsc ? 'desc' : 'asc';
    
    // Actualizar el estado local
    control.setOrder(newOrder);
    
    // Actualizar los parámetros
    control.params.delete('sort');
    control.params.append('sort', property);
    
    control.params.delete('order');
    control.params.append('order', newOrder);

    if (setFiltros) {
      setFiltros((prev) => ({
        ...prev,
        sort: property,
        order: newOrder
      }));
    }

    control.params.delete('skip');
    control.params.append('skip', '0');

    control.params.delete('take');
    control.params.append('take', control.perPage.toString());

    const apiUrl = control.params.toString() ? `${url}?${control.params.toString()}` : url;

    await control.getData(apiUrl);
  };

  return (

    <Paper>
      {titulo}
      <TableContainer sx={{ overflowX: "auto", maxWidth: "100%" }}>
        <Table
          sx={{
            tableLayout: "auto",
            width: "100%",
          }}
        >
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              {columns.map((column: columns<T>, colIndex: number) => (
                !column.omit && (
                  <TableCell
                    key={getColumnKey(column, colIndex)}
                    sortDirection={control.params.get('sort') === column.sortField ? control.order : false}
                    align={column.alling || 'left'}
                    width={column.width || "auto"}
                    sx={{
                      minWidth: column.width || 120,
                      paddingX: 2,
                      paddingY: 2,
                    }}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={control.params.get('sort') === column.sortField}
                        direction={control.params.get('sort') === column.sortField ? control.order : 'asc'}
                        onClick={createSortHandler(column.sortField)}
                      >
                        {column.name}
                        {control.params.get('sort') === column.sortField ? (
                          <Box component="span" sx={visuallyHidden}>
                            {control.order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      column.name
                    )}
                  </TableCell>
                )
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(!control.data || control.data.length === 0) ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  No se ha encontrado data
                </TableCell>
              </TableRow>
            ) : (
              control.data.map((row: T, rowIndex: number) => (
                <TableRow 
                  key={getRowKey(row, rowIndex)}
                  onContextMenu={event => handleContextMenu(event, row)}
                  hover
                >
                  {columns.map((column, colIndex) =>
                    !column.omit ? (
                      <TableCell
                        key={getColumnKey(column, colIndex)}
                        align={column.alling || 'left'}
                        sx={{
                          whiteSpace: column.wrap ? 'normal' : 'nowrap',
                          minWidth: column.width || 120,
                          paddingX: 2,
                          paddingY: 2,
                        }}
                      >
                        {column.cell ? column.cell(row) : column.selector(row)}
                      </TableCell>
                    ) : null
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions ?? [5, 10, 25, { label: 'Todos', value: control.totalRows }]}
        component="div"
        count={control.totalRows}
        rowsPerPage={control.perPage}
        page={control.page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={<Typography component="span" variant="body2" sx={{ mt: 0 }}>Filas por página</Typography>}
        labelDisplayedRows={({ from, to, count }: { from: number; to: number; count: number }) =>
          `${from}-${to} de ${count}`
        } slotProps={{
          displayedRows: {
            sx: { mt: 2 }
          }
        }}
      />
      {contextMenu && selectedRow && contextMenuRender && 
        <Menu
          open={contextMenu !== null}
          onClose={handleCloseContextMenu}
          anchorReference="anchorPosition"
          anchorPosition={{ top: contextMenu.mouseY, left: contextMenu.mouseX }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {contextMenuRender(selectedRow, handleCloseContextMenu)}
        </Menu>
      } 
    </Paper>
  )
}

export { Tabla as default }


